import { SnapshotLabelHtmlClassNames } from "@/animation/video-generator";
import { LegendContent } from "@/overlay/components/Legends/LegendContent";
import type { MapLegendProps } from "@/overlay/components/Legends/LegendsContainer";
import styles from "@/overlay/components/Legends/MapLegends.module.scss";
import { ResizeHandle } from "@/overlay/components/Legends/ResizeHandle";
import { useMapLegendProps } from "@/overlay/overlays/default/useMapLegendProps";
import { classNames } from "@/utility/jsx";
import { observeResizeWithAnimationFrame } from "@/utility/observeResizeWithAnimationFrame";
import { useLayoutEffect, useMemo, useRef, useState } from "react";

const HEIGHT_WIDTH_RATIO = 3;
const MIN_HEIGHT = 200;
const DEFAULT_HEIGHT = 300;
const MAP_RECORDING_STATIC_OVERLAY_TYPE = "map-legend";

type MapRecordingLegendsProps = {
  cellLayout: {
    gridRowEnd: number;
    gridRowStart: number;
  };
  map: {
    id: number;
    legend_size: number | null;
    layers?: any[] | undefined;
  };
};

const ignoreLegendToggle = () => undefined;
const ignoreLegendResize = () => undefined;

export function MapRecordingLegends({ cellLayout, map }: MapRecordingLegendsProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const legendProps = useMapLegendProps(map.layers);
  const visibleLegendProps = useMemo(
    () => legendProps?.filter((legendProp) => legendProp.legendVisible) ?? [],
    [legendProps],
  );
  const visibleLegendCount = visibleLegendProps.length;
  const defaultHeight = map.legend_size || DEFAULT_HEIGHT;
  const position = useMemo(
    () => (cellLayout.gridRowEnd === 97 ? "bottom" : cellLayout.gridRowStart === 1 ? "top" : undefined),
    [cellLayout],
  );
  const [dimensions, setDimensions] = useState(() => {
    const initialWidth = (defaultHeight / HEIGHT_WIDTH_RATIO) * Math.max(1, visibleLegendCount);
    return {
      height: defaultHeight,
      width: initialWidth,
    };
  });

  useLayoutEffect(() => {
    const element = containerRef.current;
    const parent = element?.parentElement;
    if (!(element && parent) || visibleLegendCount === 0) {
      return;
    }

    const updateDimensions = (parentHeight: number) => {
      const maxHeightRatio = position === "top" ? 0.55 : 0.7;
      const nextHeight = Math.round(Math.min(Math.max(MIN_HEIGHT, defaultHeight), parentHeight * maxHeightRatio));
      const nextWidth = (nextHeight / HEIGHT_WIDTH_RATIO) * visibleLegendCount;

      setDimensions((previousDimensions) => {
        if (previousDimensions.height === nextHeight && previousDimensions.width === nextWidth) {
          return previousDimensions;
        }

        return {
          height: nextHeight,
          width: nextWidth,
        };
      });
    };

    updateDimensions(parent.clientHeight);
    return observeResizeWithAnimationFrame(parent, (entry) => {
      updateDimensions(entry.contentRect.height);
    });
  }, [defaultHeight, position, visibleLegendCount]);

  if (visibleLegendCount === 0) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      style={{
        width: `${dimensions.width}px`,
        height: `${dimensions.height}px`,
        left: "0.5rem",
        bottom: "0.5rem",
        position: "absolute",
        pointerEvents: "none",
      }}
    >
      <div
        className={classNames(
          SnapshotLabelHtmlClassNames.Map,
          styles["map-legend-container"],
          position === "bottom" && styles["bottom-legend"],
          position === "top" && styles["top-legend"],
        )}
        data-recording-static-overlay-key={`map-legend-${map.id}`}
        data-recording-static-overlay-type={MAP_RECORDING_STATIC_OVERLAY_TYPE}
        data-recording-static-overlay-expected-count={String(visibleLegendCount)}
        style={{
          width: "100%",
          height: "100%",
          maxHeight: "none",
          maxWidth: "none",
          position: "static",
          left: "auto",
          bottom: "auto",
          pointerEvents: "none",
        }}
      >
        <div className={classNames("float", styles["content-container"])}>
          {visibleLegendProps.map((legendProp: MapLegendProps) => (
            <LegendContent
              key={legendProp.layerId}
              legendProp={legendProp}
              containerWidth={dimensions.width}
              visibleLegendCount={visibleLegendCount}
              toggleLegend={ignoreLegendToggle}
            />
          ))}
          <ResizeHandle
            containerRef={containerRef}
            isHidden={false}
            onDrag={ignoreLegendResize}
            onDragEnd={ignoreLegendResize}
          />
        </div>
      </div>
    </div>
  );
}
